class BundleBuilder {
  constructor(container) {
    this.container = container;
    if (!this.container) return;

    this.maxItems = parseInt(this.container.dataset.bundleSize, 10) || 3;
    this.fixedPrice = parseInt(this.container.dataset.bundleFixedPrice, 10) || 0;
    this.items = [];
    this.bundleProductId = this.container.dataset.bundleProductId || '';
    this.bundleProductHandle = this.container.dataset.bundleProductHandle || '';
    this.bundleVariantId = parseInt(this.container.dataset.bundleVariantId, 10) || 0;
    this.bundleInventoryCheck = this.parseBoolean(this.container.dataset.bundleInventoryCheck);
    this.bundleInventoryQuantity = this.parseOptionalInt(this.container.dataset.bundleInventoryQuantity);
    this.bundleQuantityRuleMax = this.parseOptionalInt(this.container.dataset.bundleQuantityRuleMax);
    this.bundlePriceCarrierVariantId = parseInt(this.container.dataset.bundlePriceCarrierVariantId, 10) || 0;
    this.bundlePriceCarrierInventoryCheck = this.parseBoolean(
      this.container.dataset.bundlePriceCarrierInventoryCheck
    );
    this.bundlePriceCarrierInventoryQuantity = this.parseOptionalInt(
      this.container.dataset.bundlePriceCarrierInventoryQuantity
    );
    this.bundlePriceCarrierQuantityRuleMax = this.parseOptionalInt(
      this.container.dataset.bundlePriceCarrierQuantityRuleMax
    );
    this.bundleProductTitle = this.container.dataset.bundleProductTitle || '';
    this.bundleKey = '';

    this.summary = this.container.querySelector('[data-bundle-summary]');
    this.countEl = this.container.querySelector('[data-bundle-count]');
    this.progressEl = this.container.querySelector('[data-bundle-progress]');
    this.totalEl = this.container.querySelector('[data-bundle-total]');
    this.packagePicker = this.container.querySelector('[data-bundle-package-picker]');
    this.packageOptionButtons = Array.from(this.container.querySelectorAll('[data-bundle-package-option]'));
    this.packageTotalEl = this.container.querySelector('[data-bundle-package-total]');
    this.activePackageOption = null;
    this.submitBtn = this.container.querySelector('[data-bundle-submit]');
    this.messageEl = this.container.querySelector('[data-bundle-message]');
    this.clearBtn = this.container.querySelector('[data-bundle-clear]');
    this.slots = this.container.querySelectorAll('[data-bundle-slot]');
    this.slotPlaceholderImage = '';
    if (this.slots.length) {
      const firstSlotImage = this.slots[0].querySelector('.bundle-summary__slot-image');
      if (firstSlotImage) this.slotPlaceholderImage = firstSlotImage.getAttribute('src') || '';
    }
    this.hasPurchasablePackage = this.isBundlePackageAvailable();
    this.submitLabel = this.submitBtn
      ? (this.submitBtn.dataset.bundleSubmitLabel || this.submitBtn.textContent.trim())
      : 'Add To Cart';
    this.soldOutLabel = this.submitBtn ? (this.submitBtn.dataset.bundleSoldOutLabel || 'Sold Out') : 'Sold Out';
    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    if (this.packageOptionButtons.length > 0) {
      const initialPackageOption = this.getInitialPackageOptionButton();
      if (initialPackageOption) {
        this.setActivePackageOption(initialPackageOption);
      }
    }

    this.bindEvents();
    this.initMobileSummary();
    this.updateUI();
  }

  bindEvents() {
    this.container.addEventListener('click', (event) => {
      const packageOptionButton = event.target.closest('[data-bundle-package-option]');
      if (packageOptionButton) {
        this.handlePackageOptionClick(packageOptionButton);
        return;
      }

      const addButton = event.target.closest('[data-bundle-add]');
      if (addButton) {
        this.handleAddClick(addButton);
        return;
      }

      const increaseButton = event.target.closest('[data-bundle-increase]');
      if (increaseButton) {
        this.handleIncreaseClick(increaseButton);
        return;
      }

      const decreaseButton = event.target.closest('[data-bundle-decrease]');
      if (decreaseButton) {
        this.handleDecreaseClick(decreaseButton);
      }
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clearAll());
    }

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', () => this.addToCart());
    }

    this.container.querySelectorAll('[data-bundle-remove]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const slot = e.target.closest('[data-bundle-slot]');
        if (!slot) return;

        const slotIndex = parseInt(slot.dataset.bundleSlot, 10) - 1;
        this.removeItemByIndex(slotIndex);
      });
    });
  }

  initMobileSummary() {
    if (!this.summary || window.innerWidth > 989) return;

    this.summary.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-summary__header')) {
        this.summary.classList.toggle('is-expanded');
      }
    });
  }

  handleAddClick(button) {
    const card = button.closest('.bundle-card');
    if (!card) return;
    this.addItemFromCard(card);
  }

  handleIncreaseClick(button) {
    const card = button.closest('.bundle-card');
    if (!card) return;
    this.addItemFromCard(card);
  }

  handleDecreaseClick(button) {
    const card = button.closest('.bundle-card');
    if (!card) return;

    const productId = card.dataset.productId;
    this.removeOneByProductId(productId);
  }

  isCardAvailable(card) {
    if (!card) return false;
    if (!this.hasPurchasablePackage) return false;
    const availableValue = String(card.dataset.productAvailable || '').trim().toLowerCase();
    if (availableValue === 'false' || availableValue === '0') return false;
    return true;
  }

  parseBoolean(value) {
    return String(value).trim().toLowerCase() === 'true';
  }

  parseOptionalInt(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  isCardInventorySelectionExhausted(card, selectedQuantity) {
    if (!card || !this.parseBoolean(card.dataset.inventoryCheck)) return false;

    const inventoryQuantity = this.parseOptionalInt(card.dataset.inventoryQuantity);
    if (inventoryQuantity === null) return false;

    return selectedQuantity >= inventoryQuantity;
  }

  getPackageOptionData(button) {
    if (!button) {
      return {
        button: null,
        variantId: '',
        title: 'デフォルト',
        priceCents: 0,
        available: true,
        inventoryCheck: false,
        inventoryQuantity: null
      };
    }

    return {
      button,
      variantId: String(button.dataset.packageVariantId || ''),
      title: String(button.dataset.packageTitle || 'デフォルト'),
      priceCents: parseInt(button.dataset.packagePriceCents, 10) || 0,
      available: this.isPackageOptionSelectable(button),
      inventoryCheck: this.parseBoolean(button.dataset.packageInventoryCheck),
      inventoryQuantity: this.parseOptionalInt(button.dataset.packageInventoryQuantity)
    };
  }

  isPackageOptionSelectable(button) {
    if (!button || button.disabled) return false;
    const availableValue = String(button.dataset.packageAvailable || 'true').trim().toLowerCase();
    if (availableValue === 'false' || availableValue === '0') return false;
    return true;
  }

  isBundlePackageAvailable() {
    const availableValue = String(this.summary?.dataset.bundlePackageAvailable || '').trim().toLowerCase();
    if (availableValue === 'true' || availableValue === '1') return true;
    if (availableValue === 'false' || availableValue === '0') return false;
    return this.packageOptionButtons.some((button) => this.isPackageOptionSelectable(button));
  }

  getInitialPackageOptionButton() {
    return this.packageOptionButtons.find(
      (button) => button.classList.contains('is-active') && this.isPackageOptionSelectable(button)
    ) || this.packageOptionButtons.find((button) => this.isPackageOptionSelectable(button)) || null;
  }

  setActivePackageOption(button) {
    if (!this.isPackageOptionSelectable(button)) return;
    const selectedOption = this.getPackageOptionData(button);
    this.packageOptionButtons.forEach((optionButton) => {
      const isActive = optionButton === button;
      optionButton.classList.toggle('is-active', isActive);
      optionButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    this.activePackageOption = selectedOption;
  }

  handlePackageOptionClick(button) {
    if (!this.isPackageOptionSelectable(button)) return;
    this.setMessage('');
    this.setActivePackageOption(button);
    this.updateUI();
  }

  addItemFromCard(card) {
    if (!this.hasPurchasablePackage) {
      this.setMessage('現在選択可能なパッケージが品切れです。', true);
      return;
    }
    if (!this.isCardAvailable(card)) return;
    if (this.items.length >= this.maxItems) return;
    if (this.isCardInventorySelectionExhausted(card, this.getProductQuantity(card.dataset.productId))) return;

    const item = {
      productId: card.dataset.productId,
      variantId: card.dataset.variantId,
      title: card.dataset.productTitle,
      price: parseInt(card.dataset.productPrice, 10) || 0,
      image: card.dataset.productImage,
      inventoryCheck: this.parseBoolean(card.dataset.inventoryCheck),
      inventoryQuantity: this.parseOptionalInt(card.dataset.inventoryQuantity)
    };

    this.items.push(item);
    this.setMessage('');
    this.updateUI();
  }

  removeOneByProductId(productId) {
    const index = [...this.items].map((item) => item.productId).lastIndexOf(productId);
    if (index === -1) return;
    this.items.splice(index, 1);
    this.setMessage('');
    this.updateUI();
  }

  removeItemByIndex(index) {
    if (index < 0 || index >= this.items.length) return;
    this.items.splice(index, 1);
    this.setMessage('');
    this.updateUI();
  }

  getProductQuantity(productId) {
    return this.items.filter((item) => item.productId === productId).length;
  }

  clearAll() {
    this.items = [];
    this.updateUI();
  }

  setMessage(message = '', isError = false) {
    if (!this.messageEl) return;

    const text = String(message || '').trim();
    this.messageEl.textContent = text;
    this.messageEl.hidden = text === '';
    this.messageEl.classList.toggle('bundle-summary__message--error', Boolean(isError && text));
  }

  getPackageExtraTotal() {
    if (!this.activePackageOption) return 0;
    return parseInt(this.activePackageOption.priceCents, 10) || 0;
  }

  updateUI() {
    const count = this.items.length;
    this.container.classList.toggle('is-bundle-sold-out', !this.hasPurchasablePackage);

    if (this.countEl) this.countEl.textContent = count;

    if (this.progressEl) {
      this.progressEl.style.width = `${(count / this.maxItems) * 100}%`;
    }

    this.slots.forEach((slot, i) => {
      const emptyEl = slot.querySelector('.bundle-summary__slot-empty');
      const filledEl = slot.querySelector('.bundle-summary__slot-filled');
      const slotImageEl = filledEl ? filledEl.querySelector('.bundle-summary__slot-image') : null;
      const item = this.items[i];

      if (item) {
        emptyEl.hidden = true;
        filledEl.hidden = false;
        if (slotImageEl) {
          slotImageEl.src = item.image || this.slotPlaceholderImage;
          slotImageEl.alt = item.title || '';
        }
      } else {
        emptyEl.hidden = false;
        filledEl.hidden = true;
        if (slotImageEl) {
          slotImageEl.src = this.slotPlaceholderImage;
          slotImageEl.alt = '';
        }
      }
    });

    const totalPrice = this.fixedPrice + this.getPackageExtraTotal();
    if (this.totalEl) {
      this.totalEl.textContent = this.formatMoney(totalPrice);
    }
    if (this.packageTotalEl) {
      this.packageTotalEl.textContent = `合計 ${this.formatMoney(totalPrice)}`;
    }

    this.container.querySelectorAll('.bundle-card').forEach((card) => {
      const productId = card.dataset.productId;
      const isAvailable = this.isCardAvailable(card);
      const qty = this.getProductQuantity(productId);
      const isInventoryExhausted = this.isCardInventorySelectionExhausted(card, qty);
      const addBtn = card.querySelector('[data-bundle-add]');
      const qtyWrap = card.querySelector('[data-bundle-quantity]');
      const qtyValue = card.querySelector('[data-bundle-qty-value]');
      const increaseBtn = card.querySelector('[data-bundle-increase]');
      const decreaseBtn = card.querySelector('[data-bundle-decrease]');

      card.classList.toggle('is-selected', qty > 0);

      if (addBtn) {
        addBtn.textContent = this.hasPurchasablePackage
          ? (addBtn.dataset.bundleAddLabel || 'Add To Box')
          : (addBtn.dataset.bundleSoldOutLabel || 'Sold Out');
        addBtn.hidden = qty > 0;
        addBtn.disabled = !isAvailable || isInventoryExhausted || count >= this.maxItems;
      }

      if (qtyWrap) {
        qtyWrap.hidden = qty === 0;
      }

      if (qtyValue) {
        qtyValue.textContent = qty;
      }

      if (increaseBtn) {
        increaseBtn.disabled = !isAvailable || isInventoryExhausted || count >= this.maxItems;
      }

      if (decreaseBtn) {
        decreaseBtn.disabled = !isAvailable || qty === 0;
      }
    });

    if (this.clearBtn) {
      this.clearBtn.disabled = count === 0;
    }

    if (this.submitBtn) {
      const requiresPackageSelection = this.packageOptionButtons.length > 0;
      const hasSelectablePackage = this.hasPurchasablePackage
        && (!requiresPackageSelection || Boolean(this.activePackageOption && this.activePackageOption.available !== false));
      this.submitBtn.classList.toggle('is-sold-out', !this.hasPurchasablePackage);
      if (!this.isSubmitting) {
        this.submitBtn.textContent = this.hasPurchasablePackage ? this.submitLabel : this.soldOutLabel;
      }
      this.submitBtn.disabled = !this.hasPurchasablePackage || count < this.maxItems || !hasSelectablePackage;
    }
  }

  hashString(value) {
    let hash = 2166136261;
    const input = String(value || '');

    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
  }

  generateBundleKey(summary = [], selectedPackage = null, bundleParentVariantId = this.bundleVariantId) {
    const itemCode = Array.isArray(summary)
      ? summary
          .map((entry) => `${entry.variantId || 'unknown'}x${entry.quantity || 0}`)
          .sort()
          .join('|')
      : '';
    const packageCode = selectedPackage && selectedPackage.variantId ? selectedPackage.variantId : 'default';
    const baseKey = [
      this.bundleProductId || this.bundleProductHandle || 'bundle',
      bundleParentVariantId || this.bundleVariantId || 'default',
      packageCode,
      itemCode
    ].join('::');

    return `bundle-${this.hashString(baseKey)}`;
  }

  formatMoney(cents) {
    const safeCents = Number.isFinite(cents) ? cents : 0;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format =
        (window.theme && (window.theme.moneyFormat || window.theme.money_with_currency_format))
        || window.Shopify.money_format
        || '${{amount}}';
      return window.Shopify.formatMoney(safeCents, format);
    }

    try {
      const currencyCode = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
        ? window.Shopify.currency.active
        : 'USD';
      const locale = document.documentElement.lang || 'en-US';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).format(safeCents / 100);
    } catch (error) {
      const amount = (safeCents / 100).toFixed(2);
      return `$${amount}`;
    }
  }

  buildSelectedItemsSummary() {
    const grouped = new Map();

    this.items.forEach((item) => {
      const key = item.productId || item.variantId || item.title;
      if (!grouped.has(key)) {
        grouped.set(key, {
          title: item.title || '',
          variantId: item.variantId || '',
          quantity: 0,
          inventoryCheck: Boolean(item.inventoryCheck),
          inventoryQuantity: item.inventoryQuantity
        });
      }

      grouped.get(key).quantity += 1;
    });

    return Array.from(grouped.values()).sort((a, b) => a.title.localeCompare(b.title));
  }

  buildBundleComponentProperties(role, extraProperties = {}, bundleKey = this.bundleKey) {
    return {
      _linked_product_id: this.bundleProductId,
      _linked_product_handle: this.bundleProductHandle,
      _linked_base_variant_id: String(this.bundleVariantId),
      _package_set_id: bundleKey,
      _bundle_key: bundleKey,
      _bundle_component_of: bundleKey,
      _bundle_component_role: role,
      ...extraProperties
    };
  }

  getBundleParentVariantId(selectedPackage) {
    const hasLimitedPackage = Boolean(selectedPackage && selectedPackage.variantId);
    if (hasLimitedPackage && this.bundlePriceCarrierVariantId) {
      return this.bundlePriceCarrierVariantId;
    }

    return this.bundleVariantId;
  }

  getBundleParentInventoryData(bundleParentVariantId) {
    if (this.bundlePriceCarrierVariantId && Number(bundleParentVariantId) === this.bundlePriceCarrierVariantId) {
      return {
        inventoryCheck: this.bundlePriceCarrierInventoryCheck,
        inventoryQuantity: this.bundlePriceCarrierInventoryQuantity,
        maxQuantity: this.bundlePriceCarrierQuantityRuleMax
      };
    }

    return {
      inventoryCheck: this.bundleInventoryCheck,
      inventoryQuantity: this.bundleInventoryQuantity,
      maxQuantity: this.bundleQuantityRuleMax
    };
  }

  getCartVariantQuantity(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      const itemVariantId = item.variant_id ?? item.id;
      if (String(itemVariantId) !== String(variantId)) return total;
      return total + (Number.parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  createInventoryError(message, shouldClearSelection = false) {
    const error = new Error(message);
    error.clearBundleSelection = shouldClearSelection;
    return error;
  }

  getDefaultPackageOverstockMessage() {
    return 'デフォルトパッケージの在庫数を超えるため、カートに追加できませんでした。選択をクリアしました。';
  }

  getInventoryRecords(summary, selectedPackage, bundleParentVariantId) {
    const parentInventory = this.getBundleParentInventoryData(bundleParentVariantId);
    const isDefaultPackageParent = !selectedPackage?.variantId && Number(bundleParentVariantId) === this.bundleVariantId;
    const records = [
      {
        variantId: String(bundleParentVariantId || ''),
        title: isDefaultPackageParent ? 'デフォルトパッケージ' : this.bundleProductTitle || 'Bundle',
        quantity: 1,
        inventoryCheck: parentInventory.inventoryCheck,
        inventoryQuantity: parentInventory.inventoryQuantity,
        maxQuantity: parentInventory.maxQuantity,
        defaultPackage: isDefaultPackageParent
      }
    ];

    summary.forEach((entry) => {
      records.push({
        variantId: String(entry.variantId || ''),
        title: entry.title || '',
        quantity: Number.parseInt(entry.quantity, 10) || 0,
        inventoryCheck: Boolean(entry.inventoryCheck),
        inventoryQuantity: entry.inventoryQuantity,
        maxQuantity: null,
        defaultPackage: false
      });
    });

    if (selectedPackage && selectedPackage.variantId) {
      records.push({
        variantId: String(selectedPackage.variantId),
        title: selectedPackage.title || 'パッケージ',
        quantity: 1,
        inventoryCheck: Boolean(selectedPackage.inventoryCheck),
        inventoryQuantity: selectedPackage.inventoryQuantity,
        maxQuantity: null,
        defaultPackage: false
      });
    }

    return records;
  }

  async validateInventoryBeforeAdd(summary, selectedPackage, bundleParentVariantId) {
    const groupedRecords = new Map();

    this.getInventoryRecords(summary, selectedPackage, bundleParentVariantId).forEach((record) => {
      if (!record.variantId) return;
      const limitQuantities = [];
      if (record.inventoryCheck && Number.isFinite(record.inventoryQuantity)) {
        limitQuantities.push(record.inventoryQuantity);
      }
      if (Number.isFinite(record.maxQuantity)) {
        limitQuantities.push(record.maxQuantity);
      }
      if (limitQuantities.length === 0) return;

      const key = String(record.variantId);
      const currentRecord = groupedRecords.get(key) || {
        variantId: key,
        title: record.title || '',
        quantity: 0,
        limitQuantity: Math.min(...limitQuantities),
        defaultPackage: Boolean(record.defaultPackage)
      };
      currentRecord.quantity += Number.parseInt(record.quantity, 10) || 0;
      currentRecord.limitQuantity = Math.min(currentRecord.limitQuantity, ...limitQuantities);
      currentRecord.defaultPackage = currentRecord.defaultPackage || Boolean(record.defaultPackage);
      groupedRecords.set(key, currentRecord);
    });

    if (groupedRecords.size === 0) return;

    let cart;
    try {
      cart = await this.fetchCart();
    } catch (error) {
      throw this.createInventoryError('在庫確認に失敗しました。時間をおいて再度お試しください。');
    }

    for (const record of groupedRecords.values()) {
      const currentQuantity = this.getCartVariantQuantity(cart, record.variantId);
      if (currentQuantity + record.quantity <= record.limitQuantity) continue;

      if (record.defaultPackage) {
        throw this.createInventoryError(this.getDefaultPackageOverstockMessage(), true);
      }
      const title = record.title ? `${record.title}の` : '';
      throw this.createInventoryError(
        `${title}在庫数を超えるため、カートに追加できませんでした。選択をクリアしました。`,
        true
      );
    }
  }

  getCartSectionIds() {
    if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return [];

    return this.cart
      .getSectionsToRender()
      .map((section) => section.id || section.section)
      .filter(Boolean);
  }

  getDisplayItemCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return cart && cart.item_count ? cart.item_count : 0;

    return cart.items.reduce((sum, item) => {
      const linkedProductId = item?.properties?._linked_product_id;
      if (linkedProductId && String(linkedProductId).trim() !== '') return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  updateCartCount(cart) {
    const displayItemCount = this.getDisplayItemCount(cart);
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = displayItemCount;
    });
  }

  async fetchCart() {
    const cartResponse = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!cartResponse.ok) {
      throw new Error('Failed to refresh cart');
    }

    return cartResponse.json();
  }

  async refreshCartAfterMutation() {
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      await cartDrawerItems.onCartUpdate();
      if (this.cart && typeof this.cart.open === 'function') {
        this.cart.open();
      }
      const cart = await this.fetchCart();
      this.updateCartCount(cart);
      return;
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      await cartItems.onCartUpdate();
      const cart = await this.fetchCart();
      this.updateCartCount(cart);
      return;
    }

    const cart = await this.fetchCart();
    this.updateCartCount(cart);
  }

  async renderCartAfterAdd(responseData, sectionIds) {
    const hasRenderableSections = this.cart
      && Array.isArray(sectionIds)
      && sectionIds.length > 0
      && responseData.sections
      && sectionIds.every((sectionId) => Boolean(responseData.sections[sectionId]));

    if (hasRenderableSections && typeof this.cart.renderContents === 'function') {
      try {
        this.cart.renderContents(responseData);
        return;
      } catch (error) {
        console.error('Bundle cart render fallback:', error);
      }
    }

    await this.refreshCartAfterMutation();
  }

  async addToCart() {
    if (!this.hasPurchasablePackage) {
      this.setMessage('現在選択可能なパッケージが品切れです。', true);
      return;
    }
    if (this.items.length < this.maxItems || !this.submitBtn) return;
    if (this.packageOptionButtons.length > 0 && (!this.activePackageOption || this.activePackageOption.available === false)) {
      this.setMessage('購入可能なパッケージを選択してください。', true);
      return;
    }
    if (this.isSubmitting) return;
    if (!this.bundleVariantId) {
      if (typeof window.showNotification === 'function') {
        window.showNotification('Bundle variant is not available', 'error');
      }
      return;
    }

    this.isSubmitting = true;
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Adding...';
    this.setMessage('');

    const summary = this.buildSelectedItemsSummary();
    const selectedPackage = this.activePackageOption || this.getPackageOptionData(null);
    const bundleParentVariantId = this.getBundleParentVariantId(selectedPackage);
    const bundleKey = this.generateBundleKey(summary, selectedPackage, bundleParentVariantId);
    this.bundleKey = bundleKey;
    const summaryText = summary.map((entry) => `${entry.title} × ${entry.quantity}`).join(', ');
    const summaryCode = summary.map((entry) => `${entry.variantId}x${entry.quantity}`).join('|');

    const properties = {
      Items: summaryText,
      _bundle_key: bundleKey,
      _bundle_size: `${this.maxItems}`,
      _bundle_items: summaryCode,
      _bundle_component_items: summaryCode,
      _package_set_id: bundleKey,
      _bundle_package_variant_id: selectedPackage.variantId || 'default',
      _bundle_package_label: selectedPackage.title || 'デフォルト',
      _bundle_inventory_variant_id: String(this.bundleVariantId),
      パッケージ: selectedPackage.title || 'デフォルト'
    };

    if (bundleParentVariantId !== this.bundleVariantId) {
      properties._bundle_price_carrier_variant_id = String(bundleParentVariantId);
    }

    if (this.bundleProductId) properties._bundle_parent_id = this.bundleProductId;
    if (this.bundleProductTitle) properties._bundle_parent_title = this.bundleProductTitle;
    if (this.bundleProductHandle) properties._bundle_parent_handle = this.bundleProductHandle;

    const items = [
      {
        id: bundleParentVariantId,
        quantity: 1,
        properties
      }
    ];

    summary.forEach((entry) => {
      const componentVariantId = parseInt(entry.variantId, 10);
      if (!componentVariantId || entry.quantity <= 0) return;

      items.push({
        id: componentVariantId,
        quantity: entry.quantity,
        properties: this.buildBundleComponentProperties('fragrance', {
          _bundle_component_qty: String(entry.quantity),
          _bundle_component_title: entry.title || '',
          _bundle_display_variant_id: String(componentVariantId),
          _bundle_parent_id: this.bundleProductId,
          _bundle_parent_title: this.bundleProductTitle,
          '対象セット': this.bundleProductTitle || ''
        }, bundleKey)
      });
    });

    if (selectedPackage.variantId) {
      items.push({
        id: selectedPackage.variantId,
        quantity: 1,
        properties: this.buildBundleComponentProperties('package', {
          _bundle_component_qty: '1',
          _bundle_package_variant_id: selectedPackage.variantId,
          _bundle_package_label: selectedPackage.title || 'デフォルト',
          '対象商品': this.bundleProductTitle || ''
        }, bundleKey)
      });
    }

    const sectionIds = this.getCartSectionIds();
    const requestBody = { items };
    if (sectionIds.length > 0) {
      requestBody.sections = sectionIds;
      requestBody.sections_url = window.location.pathname;
      if (this.cart && typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }
    }

    try {
      await this.validateInventoryBeforeAdd(summary, selectedPackage, bundleParentVariantId);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        const errorMessage = responseData.description || responseData.message || 'Failed to add to cart';
        const isDefaultPackageOverstock =
          !selectedPackage.variantId && /最大数|在庫|数量|品切れ|stock|sold|quantity|available|maximum/i.test(errorMessage);
        if (isDefaultPackageOverstock) {
          throw this.createInventoryError(this.getDefaultPackageOverstockMessage(), true);
        }
        const shouldClearSelection = /最大数|在庫|数量|品切れ|stock|sold|quantity|available|maximum/i.test(errorMessage);
        throw this.createInventoryError(errorMessage, shouldClearSelection);
      }

      if (!responseData.key && Array.isArray(responseData.items) && responseData.items.length) {
        responseData.key = responseData.items[0].key;
      }

      this.clearAll();
      this.bundleKey = '';

      if (this.cart && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }

      try {
        await this.renderCartAfterAdd(responseData, sectionIds);
      } catch (error) {
        console.error('Bundle cart refresh failed:', error);
      }

      if (typeof window.showNotification === 'function') {
        window.showNotification('Bundle added to cart!', 'success');
      }
      this.setMessage('Added to cart.');

      this.submitBtn.textContent = 'Added!';
      setTimeout(() => {
        this.submitBtn.textContent = this.submitLabel;
        this.submitBtn.disabled = true;
      }, 1500);
    } catch (error) {
      console.error('Bundle add to cart error:', error);
      if (error.clearBundleSelection) {
        this.bundleKey = '';
        this.clearAll();
      }
      this.submitBtn.textContent = this.submitLabel;
      this.submitBtn.disabled = this.items.length < this.maxItems;
      this.setMessage(error.message || 'カートに追加できませんでした。数量または在庫をご確認ください。', true);

      if (typeof window.showNotification === 'function') {
        window.showNotification(error.message || 'Error adding bundle to cart', 'error');
      }
    } finally {
      this.isSubmitting = false;
    }
  }
}

const initBundleBuilders = (root = document) => {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('.bundle-page').forEach((container) => {
    if (container.dataset.bundleBuilderBound === 'true') return;
    container.dataset.bundleBuilderBound = 'true';
    new BundleBuilder(container);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBundleBuilders());
} else {
  initBundleBuilders();
}

document.addEventListener('shopify:section:load', (event) => {
  initBundleBuilders(event.target || document);
});
